import { create } from 'zustand';
import api from '../config/axios';
import { INVOICES } from '../config/apiPaths';
import { Invoice, InvoiceCreate, InvoiceUpdate } from '../types/invoice';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';

interface InvoiceStore {
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  isLoading: boolean;
  error: string | null;

  fetchInvoices: () => Promise<void>;
  fetchInvoiceById: (id: number) => Promise<void>;
  createInvoice: (invoice: InvoiceCreate) => Promise<Invoice>;
  updateInvoice: (id: number, invoice: InvoiceUpdate) => Promise<Invoice>;
  deleteInvoice: (id: number) => Promise<void>;

  getInvoiceById: (id: number) => Invoice | undefined;
  clearError: () => void;
}

export const useInvoiceStore = create<InvoiceStore>((set, get) => ({
  invoices: [],
  currentInvoice: null,
  isLoading: false,
  error: null,

  fetchInvoices: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(INVOICES.ALL);
      set({ invoices: response.data, isLoading: false });
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to fetch invoices';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  fetchInvoiceById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(INVOICES.BY_ID(id));
      set((state) => ({
        invoices: [...state.invoices.filter((i) => i.id !== id), response.data],
        currentInvoice: response.data,
        isLoading: false,
      }));
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to fetch invoice';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  createInvoice: async (invoice: InvoiceCreate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(INVOICES.ALL, invoice);
      set((state) => ({ invoices: [...state.invoices, response.data], isLoading: false }));

      notifyEntityCreated('invoice', {
        id: response.data.id,
        title: response.data.title,
        subject: response.data.number,
      });

      return response.data;
    } catch (error: any) {
      // Enhanced error logging for debugging
      console.error('Invoice creation failed:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        error: error?.response?.data?.error,
        message: error?.response?.data?.message,
      });

      // Check for duplicate receipt number error
      const errorData = error?.response?.data;
      const isDuplicateReceiptNumber =
        errorData?.error?.includes('duplicate key value violates unique constraint "idx_invoice_receipt_number"') ||
        errorData?.error?.includes('idx_invoice_receipt_number') ||
        (error?.response?.status === 500 && errorData?.error?.includes('SQLSTATE 23505'));

      let message: string;
      if (isDuplicateReceiptNumber) {
        message = 'Failed to create invoice: Duplicate receipt number detected. This is a server issue with receipt number generation. Please try again or contact support if the problem persists.';
      } else {
        message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to create invoice';
      }

      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  updateInvoice: async (id: number, invoice: InvoiceUpdate) => {
    set({ isLoading: true, error: null });
    try {
      // Enhanced debugging: log the payload being sent
      console.log('Updating invoice:', {
        id,
        url: INVOICES.BY_ID(id),
        payload: invoice,
        payloadKeys: Object.keys(invoice),
        itemsCount: invoice.items?.length || 0
      });

      const response = await api.put(INVOICES.BY_ID(id), invoice);

      console.log('Invoice update successful:', response.data);

      set((state) => ({
        invoices: state.invoices.map((i) => (i.id === id ? response.data : i)),
        currentInvoice: state.currentInvoice?.id === id ? response.data : state.currentInvoice,
        isLoading: false,
      }));

      notifyEntityUpdated('invoice', {
        id: response.data.id,
        title: response.data.title,
        subject: response.data.number,
      });

      return response.data;
    } catch (error: any) {
      // Enhanced error logging
      console.error('Invoice update failed:', {
        id,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        errorData: error?.response?.data,
        message: error?.response?.data?.message,
        errors: error?.response?.data?.errors,
        url: INVOICES.BY_ID(id),
        payload: invoice
      });

      const message = error?.response?.data?.message || 'Failed to update invoice';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  deleteInvoice: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(INVOICES.BY_ID(id));
      set((state) => ({
        invoices: state.invoices.filter((i) => i.id !== id),
        currentInvoice: state.currentInvoice?.id === id ? null : state.currentInvoice,
        isLoading: false,
      }));
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to delete invoice';
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  getInvoiceById: (id: number) => get().invoices.find((i) => i.id === id),
  clearError: () => set({ error: null }),
}));
